import { useState, useEffect, useMemo, type JSX } from 'react'
import { Column } from 'primereact/column'
import {
  Users, Shield, CheckCircle2, XCircle, Search, RefreshCw,
  UserCog, Plus, ShieldCheck,
} from 'lucide-react'
import { api } from '../../api/client.js'
import DataTable from '../../components/ui/DataTable.js'
import Select from '../../components/ui/Select.js'

interface PlatformAdmin {
  id: string
  username: string
  email: string
  firstName: string | null
  lastName: string | null
  status: boolean
  createdAt: string
  roles: Array<{ roleId: string; roleName: string }>
}

interface PlatformRole {
  id: string
  name: string
  description: string | null
}

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Users
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  )
}

/* ─── Role Badge with Revoke ─── */
function RoleBadge({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf]/5 px-2.5 py-1 text-[10px] font-semibold text-[#3454cf]">
      {name}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        className="h-3.5 w-3.5 flex items-center justify-center rounded-full hover:bg-[#3454cf]/10 transition-colors"
        title="Revoke role"
      >
        <XCircle className="h-3 w-3" />
      </button>
    </span>
  )
}

export default function PlatformAdminRolesPage(): JSX.Element {
  const [admins, setAdmins] = useState<PlatformAdmin[]>([])
  const [roles, setRoles] = useState<PlatformRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedAdmin, setSelectedAdmin] = useState<PlatformAdmin | null>(null)
  const [assigningRole, setAssigningRole] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')

  useEffect(() => {
    loadAdmins()
    loadRoles()
  }, [])

  const loadAdmins = async (): Promise<void> => {
    try {
      setLoading(true)
      const resp = await api.get<{ admins: PlatformAdmin[] }>('/platform/admins?limit=100')
      setAdmins(resp.admins)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const loadRoles = async (): Promise<void> => {
    try {
      const resp = await api.get<{ roles: PlatformRole[] }>('/platform/roles')
      setRoles(resp.roles)
    } catch (err) {
      console.error('Failed to load roles:', err)
    }
  }

  const assignRole = async (adminId: string, roleId: string): Promise<void> => {
    if (!roleId) return
    try {
      setAssigningRole(true)
      await api.post(`/platform/admins/${adminId}/roles`, { roleId })
      await loadAdmins()
      setSelectedAdmin(null)
      setSelectedRoleId('')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setAssigningRole(false)
    }
  }

  const revokeRole = async (adminId: string, roleId: string): Promise<void> => {
    try {
      await api.delete(`/platform/admins/${adminId}/roles/${roleId}`)
      await loadAdmins()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return admins
    return admins.filter(a =>
      a.username.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      (a.firstName && a.firstName.toLowerCase().includes(q)) ||
      (a.lastName && a.lastName.toLowerCase().includes(q)) ||
      a.roles.some(r => r.roleName.toLowerCase().includes(q)),
    )
  }, [admins, search])

  const totalRoles = useMemo(() => roles.length, [roles])
  const activeAdmins = useMemo(() => admins.filter(a => a.status).length, [admins])
  const adminsWithRoles = useMemo(() => admins.filter(a => a.roles.length > 0).length, [admins])

  return (
    <div className="w-full space-y-6 px-4 pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Platform Admin Roles</h1>
          <p className="text-sm text-gray-400 mt-1">Manage platform admin role assignments.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Admins" value={admins.length} icon={Users} accent="#3454cf" />
        <StatCard label="Active Admins" value={activeAdmins} icon={CheckCircle2} accent="#10b981" />
        <StatCard label="With Roles" value={adminsWithRoles} icon={ShieldCheck} accent="#f6931c" />
        <StatCard label="Available Roles" value={totalRoles} icon={Shield} accent="#8b5cf6" />
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-100 px-5 py-3">
          <p className="text-sm text-red-600 font-medium">{error}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
          <input
            type="text"
            placeholder="Search admins by name, email or role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 font-medium">{visible.length} admins</span>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={() => { setSearch(''); void loadAdmins() }}
            className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={visible}
        loading={loading}
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        emptyMessage="No admins found."
      >
        <Column
          header="Admin"
          body={(admin: PlatformAdmin) => (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                <UserCog className="h-5 w-5 text-[#3454cf]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {admin.firstName && admin.lastName ? `${admin.firstName} ${admin.lastName}` : admin.username}
                </p>
                <p className="text-xs text-gray-300">{admin.username}</p>
              </div>
            </div>
          )}
          style={{ width: '22%' }}
        />
        <Column
          header="Email"
          body={(admin: PlatformAdmin) => (
            <span className="text-sm text-gray-600">{admin.email}</span>
          )}
          style={{ width: '20%' }}
        />
        <Column
          header="Assigned Roles"
          body={(admin: PlatformAdmin) => (
            <div className="flex flex-wrap gap-1.5">
              {admin.roles.map((role) => (
                <RoleBadge key={role.roleId} name={role.roleName} onRemove={() => revokeRole(admin.id, role.roleId)} />
              ))}
              {admin.roles.length === 0 && (
                <span className="text-xs text-gray-300 italic">No roles assigned</span>
              )}
            </div>
          )}
        />
        <Column
          header="Status"
          body={(admin: PlatformAdmin) => <StatusBadge active={admin.status} />}
          style={{ width: '200px' }}
        />
        <Column
          header="Actions"
          body={(admin: PlatformAdmin) => (
            <button
              onClick={() => setSelectedAdmin(admin)}
              className="inline-flex items-center gap-1.5 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-xs font-semibold px-3 py-1.5 rounded-sm transition-all"
            >
              <Plus className="h-3 w-3" />
              Assign Role
            </button>
          )}
          style={{ width: '160px' }}
        />
      </DataTable>

      {/* Assign Role Dialog */}
      {selectedAdmin && (
        <div className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Dialog Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Assign Role</h3>
                  <p className="text-xs text-gray-400">{selectedAdmin.username}</p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedAdmin(null); setSelectedRoleId('') }}
                className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>

            {/* Dialog Body */}
            <div className="px-6 py-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Select Role <span className="text-red-500">*</span></label>
                <Select
                  ariaLabel="Select role"
                  leftIcon={<Shield />}
                  value={selectedRoleId}
                  onChange={setSelectedRoleId}
                >
                  <option value="">Select a role...</option>
                  {roles
                    .filter((role) => !selectedAdmin.roles.some((r) => r.roleId === role.id))
                    .map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                </Select>
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={() => { setSelectedAdmin(null); setSelectedRoleId('') }}
                className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-white transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => assignRole(selectedAdmin.id, selectedRoleId)}
                disabled={!selectedRoleId || assigningRole}
                className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all"
              >
                {assigningRole ? 'Assigning...' : 'Assign Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
